import React, { useEffect, useState } from "react";
import {
  Table,
  Tbody,
  Thead,
  Tr,
  Td,
  Th,
  TableContainer,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Box,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { BASE_URL } from "../../../Globalapi/Globalapi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Report = () => {
  const { user_id } = useParams();
  const [examResults, setExamResults] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState({});
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [academicId, setAcademicId] = useState(
    sessionStorage.getItem("Academic")
  );
  useEffect(() => {
    const handleStorageChange = () => {
      setAcademicId(sessionStorage.getItem("Academic"));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const fetchExamResults = async () => {
      try {
        const academicId = sessionStorage.getItem("Academic"); // Get academicId

        const [examResponse, categoryResponse] = await Promise.all([
          axios.post(
            `${BASE_URL}/student/exam_result_graph.php`,
            { pass: user_id }
          ),
          axios.get(`${BASE_URL}/admin/exam/exam_category.php`),
        ]);

        console.log("Exam ....Response:", examResponse);

        if (examResponse.data.success && categoryResponse.data.success) {
          const results = examResponse.data.data || [];

          // Filter by academic year and is_class_test
          const filteredExamResults = results.filter(
            (result) =>
              result.is_class_test === "1" &&
              result.academic_year_id === academicId
          );
          setExamResults(filteredExamResults);

          const categoryMap = categoryResponse.data.data.reduce((acc, item) => {
            acc[item.id] = item.category;
            return acc;
          }, {});
          setCategories(categoryMap);

          const uniqueSubjects = Array.from(
            new Set(filteredExamResults.map((result) => result.subject))
          );
          setSubjects(uniqueSubjects);

          const categoriesWithData = Object.keys(categoryMap).filter(
            (categoryId) =>
              filteredExamResults.some(
                (result) => result.exam_category_id === categoryId
              )
          );
          setFilteredCategories(categoriesWithData);

          if (filteredExamResults.length === 0) {
            setError("No Data Available");
          } else {
            setError("");
          }
        } else {
          setError("No Data Available");
        }
      } catch (error) {
        setError("Error fetching data: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchExamResults();
  }, [user_id,academicId]); // Dependency ensures data fetches when user_id changes

  if (loading) {
    return <Spinner size="xl" />;
  }

  // Prepare data for the chart
  const subjectsForChart = [
    ...new Set(examResults.map((item) => item.subject)),
  ];
  const chartData = {
    labels: filteredCategories.map((categoryId) => categories[categoryId]),
    datasets: subjectsForChart.map((subject, index) => ({
      label: subject,
      data: filteredCategories.map((categoryId) => {
        const subjectResults = examResults.filter(
          (result) =>
            result.subject === subject && result.exam_category_id === categoryId
        );
        const averageMark =
          subjectResults.reduce(
            (sum, result) => sum + parseFloat(result.student_scored_marks || 0),
            0
          ) / (subjectResults.length || 1);
        return averageMark;
      }),
      backgroundColor: `rgba(${index * 50 + 100}, 99, 132, 0.2)`,
      borderColor: `rgba(${index * 50 + 100}, 99, 132, 1)`,
      borderWidth: 2,
      pointRadius: 5,
      fill: true,
    })),
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Average Marks per Term by Subject" },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Average Marks" } },
      x: { title: { display: true, text: "Exam Categories" } },
    },
  };

  return (
    <Tabs isFitted variant="enclosed" colorScheme="teal">
      <TabList>
        <Tab>Results Table</Tab>
        <Tab>Graph</Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          {examResults.length > 0 ? (
            <TableContainer maxWidth="672px" margin="0 auto">
              <Table
                sx={{
                  "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
                }}
                size="sm"
                border="1px solid #d4c2c2"
                width="100%"
              >
                <Thead>
                  <Tr>
                    <Th
                      style={{
                        textAlign: "center",
                        fontWeight: "bold",
                        border: "1px solid #d4c2c2",
                      }}
                      rowSpan={2}
                    >
                      Subjects
                    </Th>
                    <Th
                      borderBottom="1px"
                      borderBottomColor="rgb(212, 194, 194)"
                    >
                      Details
                    </Th>
                    {filteredCategories.map((categoryId) => (
                      <Th
                        key={categoryId}
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          border: "1px solid #d4c2c2",
                        }}
                      >
                        {categories[categoryId]}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {subjects.map((subject) => (
                    <React.Fragment key={subject}>
                      <Tr>
                        <Td
                          rowSpan={2}
                          fontWeight="bold"
                          border="1px solid #d4c2c2"
                          textAlign="center"
                        >
                          {subject}
                        </Td>
                        <Td
                          fontWeight="bold"
                          border="1px solid #d4c2c2"
                          textAlign="center"
                        >
                          Date
                        </Td>
                        {filteredCategories.flatMap((categoryId) => {
                          const subjectResults = examResults.filter(
                            (result) =>
                              result.subject === subject &&
                              result.exam_category_id === categoryId
                          );
                          return Array.from({ length: 5 }).map((_, index) => (
                            <Td
                              key={`${categoryId}_date_${index}`}
                              border="1px solid #d4c2c2"
                              textAlign="center"
                            >
                              {subjectResults[index]?.date_of_exam || "-"}
                            </Td>
                          ));
                        })}
                      </Tr>
                      <Tr>
                        <Td
                          fontWeight="bold"
                          border="1px solid #d4c2c2"
                          textAlign="center"
                        >
                          Mark
                        </Td>
                        {filteredCategories.flatMap((categoryId) => {
                          const subjectResults = examResults.filter(
                            (result) =>
                              result.subject === subject &&
                              result.exam_category_id === categoryId
                          );
                          return Array.from({ length: 5 }).map((_, index) => (
                            <Td
                              key={`${categoryId}_mark_${index}`}
                              border="1px solid #d4c2c2"
                              textAlign="center"
                            >
                              {subjectResults[index]?.student_scored_marks ||
                                "-"}
                            </Td>
                          ));
                        })}
                      </Tr>
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Text
              textAlign="center"
              fontSize="lg"
              fontWeight="bold"
              color="gray.500"
            >
              No Data Available
            </Text>
          )}
        </TabPanel>

        <TabPanel>
          {examResults.length > 0 ? (
            <Box width="600px" mx="auto">
              <Line data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Text
              textAlign="center"
              fontSize="lg"
              fontWeight="bold"
              color="gray.500"
            >
              No Data Available
            </Text>
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default Report;
